import { useCallback, useEffect } from 'react';

import shallow from 'zustand/shallow';
import { useRouteHandler } from '@/logic/state';
import { useRouter } from 'next/router';

interface RouteChangeHandlerProps {
  onRouteChangeStart?: (nextPath: string) => void;
  onRouteChangeComplete?: (nextPath: string) => void;
  onBeforeUnload?: () => void;
}

export const useRouteChangeHandler = ({
  onRouteChangeStart,
  onRouteChangeComplete,
  onBeforeUnload,
}: RouteChangeHandlerProps) => {
  const router = useRouter();

  const setRouteHandler = useRouteHandler(s => s.setRouteHandler, shallow);

  const removeOnRouteChangeStartListener = useCallback(() => {
    router.events.off('routeChangeStart', onRouteChangeStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRouteChangeStart]);

  const removeOnRouteChangeCompleteListener = useCallback(() => {
    router.events.off('routeChangeComplete', onRouteChangeComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRouteChangeComplete]);

  const removeOnBeforeUnloadListener = useCallback(() => {
    window.removeEventListener('beforeunload', onBeforeUnload);
  }, [onBeforeUnload]);

  useEffect(() => {
    router.events.on('routeChangeStart', onRouteChangeStart);
    router.events.on('routeChangeComplete', onRouteChangeComplete);
    window.addEventListener('beforeunload', onBeforeUnload);

    setRouteHandler({
      removeOnRouteChangeStartListener,
      removeOnRouteChangeCompleteListener,
      removeOnBeforeUnloadListener,
    });

    return () => {
      removeOnRouteChangeStartListener();
      removeOnBeforeUnloadListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRouteChangeStart, onRouteChangeComplete, onBeforeUnload]);
};
